/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useState } from 'react';
import { LayoutItem } from '@oneaudi/unified-web-components';
import { InitialStateContextProvider } from './context/InitialStateContext';
import type { AsyncStateHolder, InitialState } from '../@types/FeatureAppTypes';
import { useLogger, useModalOpenState } from './context/Context';
import LeanMoxxView from './components/LeanMoxxView/LeanMoxxView';
import { useTrackingManager } from './context/useTrackingManager';
import EditModeWarning from './components/edit-mode-warning/EditModeWarning';

const FeatureApp: React.FC = () => {
  const [isModalOpen, { closeModal }] = useModalOpenState();

  useEffect(() => {
    let mounted = true;
    const handleResize = () => {
      if (mounted && window.innerWidth >= 768) {
        closeModal();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <LayoutItem className={isModalOpen ? 'open' : ''}>
      <LeanMoxxView />
    </LayoutItem>
  );
};

interface Props {
  readonly asyncStateHolder: AsyncStateHolder;
}

const AsyncFeatureApp: React.FunctionComponent<Props> = ({ asyncStateHolder }: Props) => {
  // when asyncStateHolder is an object it represents the serialized
  // state coming from the server ready to be used as the
  // initial state
  const [state, setState] = useState<InitialState | undefined>(
    typeof asyncStateHolder === 'object' ? asyncStateHolder : undefined,
  );

  const logger = useLogger();
  const trackingManager = useTrackingManager();

  useEffect(() => {
    let mounted = true;
    trackingManager.ready(__FEATURE_APP_VERSION__);

    // trackingManager.ready(__FEATURE_APP_VERSION__);
    // when asyncStateHolder is a function it means the state could not be properly serialized by the server and it is
    // not available on the client. In that case this effect will try to
    // fetch the state as soon as the component is mounted on the client.
    if (typeof asyncStateHolder === 'function') {
      logger?.info('SSR did not serialize any state');
      // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/no-confusing-void-expression
      asyncStateHolder().then((updatedState) => mounted && setState(updatedState));
    } else {
      logger?.info('SSR serialized state: ', asyncStateHolder);
    }

    return () => {
      mounted = false;
    };
  }, []);

  if (!state) {
    return null;
  }
  return (
    <InitialStateContextProvider initialState={state}>
      <EditModeWarning />
      <FeatureApp />
    </InitialStateContextProvider>
  );
};

export default AsyncFeatureApp;
